import {
  checkbox as checkboxPrompt,
  confirm as confirmPrompt,
  input as inputPrompt,
  search as searchPrompt,
  select as selectPrompt,
} from "@inquirer/prompts";
import pc from "picocolors";

export type TuiChoice<Value extends string> = {
  value: Value;
  name: string;
  description?: string;
  checked?: boolean;
  disabled?: boolean | string;
};

export function noriBanner(color = true): string {
  const cyan = color ? pc.cyan : (value: string) => value;
  const bold = color ? pc.bold : (value: string) => value;
  const dim = color ? pc.dim : (value: string) => value;
  return [
    cyan("╭────────────────────────────────────────╮"),
    cyan("│") + bold("       / \\__        N O R I  🐶       ") + cyan("│"),
    cyan("│") + bold("      (    @\\___    headless companion ") + cyan("│"),
    cyan("│") + dim("      /         O    warm • loyal • able") + cyan("│"),
    cyan("│") + dim("     /   (_____/     CLI • apps • agents") + cyan("│"),
    cyan("│") + dim("    /_____/   U                         ") + cyan("│"),
    cyan("╰────────────────────────────────────────╯"),
  ].join("\n");
}

export function renderNoriBanner(options: { write?: (value: string) => void; color?: boolean } = {}): void {
  (options.write ?? ((value) => process.stdout.write(value)))(`${noriBanner(options.color ?? true)}\n`);
}

export function requireInteractiveTui(json = false): void {
  if (json || !process.stdin.isTTY || !process.stdout.isTTY) {
    throw Object.assign(new Error("This workflow requires an interactive terminal and is unavailable with --json or redirected input. Use the corresponding list/get/action command for automation."), { code: "INTERACTIVE_TTY_REQUIRED" });
  }
}

export function promptWasCancelled(error: unknown): boolean {
  return (error as Error)?.name === "ExitPromptError";
}

export async function tuiSelect<Value extends string>(
  message: string,
  choices: ReadonlyArray<TuiChoice<Value>>,
  defaultValue?: Value,
): Promise<Value> {
  return selectPrompt({ message, choices, ...(defaultValue ? { default: defaultValue } : {}), pageSize: 12, loop: false } as never) as Promise<Value>;
}

export async function tuiSearch<Value extends string>(
  message: string,
  choices: ReadonlyArray<TuiChoice<Value>>,
  defaultValue?: Value,
): Promise<Value> {
  return searchPrompt({
    message,
    ...(defaultValue ? { default: defaultValue } : {}),
    pageSize: 12,
    source: async (term: string | undefined) => {
      const query = term?.trim().toLowerCase() ?? "";
      if (!query) return choices.slice(0, 100);
      return choices.filter((choice) => `${choice.name} ${choice.value} ${choice.description ?? ""}`.toLowerCase().includes(query)).slice(0, 100);
    },
  } as never) as Promise<Value>;
}

export async function tuiCheckbox<Value extends string>(message: string, choices: ReadonlyArray<TuiChoice<Value>>): Promise<Value[]> {
  return checkboxPrompt({ message, choices, pageSize: 10, loop: false } as never) as Promise<Value[]>;
}

export async function tuiInput(
  message: string,
  options: { defaultValue?: string; validate?: (value: string) => boolean | string } = {},
): Promise<string> {
  return inputPrompt({
    message,
    ...(options.defaultValue ? { default: options.defaultValue } : {}),
    ...(options.validate ? { validate: options.validate } : {}),
  });
}

export async function tuiConfirm(message: string, defaultValue = false): Promise<boolean> {
  return confirmPrompt({ message, default: defaultValue });
}

export function tuiNote(message: string): void {
  process.stdout.write(`${pc.dim("│")} ${message.replaceAll("\n", `\n${pc.dim("│")} `)}\n`);
}
